/**
 * The stream reducer. Pure: `(state, frame, at) => state`, with a batch
 * variant that applies one chunk's worth of frames on a single copy.
 *
 * Everything the client believes about a stream lives in `StreamState`, and
 * every change to that belief happens here, in one place, in response to one
 * decoded frame at a time. The machine wraps this in transport and timers, and
 * the conformance runner drives it headlessly, but neither of them contains
 * stream logic. That split is the point of the package: what CI proves about
 * the reducer, it proves about the code the browser runs.
 *
 * The nine rules, each with a dedicated test:
 *
 *   1. Dedupe: a repeated id is discarded whole, with a duplicate marker.
 *   2. Stale: an id at or below the cursor that was never seen is discarded.
 *   3. Gap: an id above cursor + 1 is applied, a gap marker is appended and
 *      hasGap is set. Never interpolate.
 *   4. Malformed: skipped with a marker. Three in a row terminate with the
 *      code `unknown`.
 *   5. Unknown event type: ignored with a marker, never a throw.
 *   6. Replace: text becomes `content ?? ''`, redaction records categories
 *      and the previous length, the previous text is not retained.
 *   7. Terminal absorption: after done, error, or cancelled, every further
 *      frame is discarded with a post_terminal marker.
 *   8. Resume identity: replayed frames fall to rules 1 and 2, so text after
 *      any number of reconnects is byte identical to an uninterrupted run.
 *   9. Three strikes: parse failures are counted per event id across
 *      reconnects. On the third, a permanent gap is recorded, the cursor
 *      advances past the poisoned id, and the stream continues. See
 *      docs/DECISIONS.md entry 14.
 *
 * One inference is worth naming. The codec drops the pending id from a
 * malformed frame, because a cursor for a frame that was never applied is a
 * cursor that lies. The reducer therefore keys rule 9 by the id the frame
 * must have occupied, `lastEventId + 1`: the cursor holds on a malformed
 * frame, so a replay fails at the same key on every attempt, which is exactly
 * the property the count needs.
 */

import {
  toNormalized,
  type ChatErrorCode,
  type DecodedFrame,
  type MessageReasoning,
  type ToolCall,
  type UsageEvent,
  type WireBackedEvent,
} from '@harness/protocol';

export type StreamPhase =
  | 'idle'
  | 'connecting'
  | 'queued'
  | 'thinking'
  | 'streaming'
  | 'reconnecting'
  | 'stalled'
  | 'done'
  | 'error'
  | 'cancelled';

const TERMINAL_PHASES: readonly StreamPhase[] = ['done', 'error', 'cancelled'];

export function isTerminalPhase(phase: StreamPhase): boolean {
  return TERMINAL_PHASES.includes(phase);
}

/**
 * One entry in the append only timeline. This is the record the wire tape and
 * the inspector draw from, and the record the invariants are checked against,
 * so nothing is dropped silently: every frame that arrives leaves exactly one
 * mark here, applied or not.
 */
export type TraceEntry =
  | { kind: 'frame'; at: number; id: number | null; type: WireBackedEvent['type']; bytes: number }
  | { kind: 'keepalive'; at: number }
  | { kind: 'sentinel'; at: number }
  | { kind: 'duplicate'; at: number; id: number }
  | { kind: 'stale'; at: number; id: number }
  | { kind: 'gap'; at: number; fromId: number; toId: number; permanent: boolean }
  | { kind: 'malformed'; at: number; reason: string; failures: number }
  | { kind: 'unknown_event'; at: number; event: string; id: number | null }
  | { kind: 'post_terminal'; at: number; description: string }
  | {
      kind: 'replace';
      at: number;
      charactersRemoved: number;
      categories: readonly string[];
      bare: boolean;
    }
  | { kind: 'reconnect'; at: number; attempt: number; lastEventId: number }
  | { kind: 'watchdog'; at: number; watchdog: 'byte' | 'content' }
  | { kind: 'cancelled'; at: number }
  | { kind: 'retry'; at: number };

export interface QueueState {
  position: number;
  estimatedWaitSeconds: number;
}

export interface StreamErrorState {
  code: ChatErrorCode;
  message?: string;
  /** Client state, never wire state. Derived from a Retry-After header. */
  retryAfterSeconds?: number;
}

export interface RedactionState {
  categories: readonly string[];
  replacedAt: number;
  previousLength: number;
  /**
   * True when the replace carried no content and no categories, which is what
   * the wire `replace` event produces. The UI distinguishes the two because
   * they mean different things to the reader: a retraction with a stated
   * reason, or a bare clear.
   */
  bare: boolean;
}

export interface ClientToolCallRecord {
  name: string;
  args: Record<string, unknown>;
  callId?: string;
}

export interface StreamState {
  phase: StreamPhase;
  streamId: string | null;
  /** Highest id successfully applied. The resume cursor. */
  lastEventId: number;
  /** The visible assistant text. */
  text: string;
  reasoning: MessageReasoning | null;
  toolCalls: ToolCall[];
  queue: QueueState | null;
  usage: UsageEvent | null;
  error: StreamErrorState | null;
  redaction: RedactionState | null;
  /** Truncation reason when the server cut the response short. Text is kept. */
  truncated: string | null;
  /** The latest free form phase string from `generation_status`. */
  generationPhase: string | null;
  clientToolCalls: ClientToolCallRecord[];
  reconnects: number;
  seenIds: Set<number>;
  timeline: TraceEntry[];
  /** True once any id was skipped. The UI never presents a gap as continuous text. */
  hasGap: boolean;
  /** Consecutive malformed frames on the current connection. Rule 4. */
  malformedStreak: number;
  /** Parse failures per inferred event id, across reconnects. Rule 9. */
  malformedFailures: Map<number, number>;
  /**
   * When the current reasoning window opened. Meaningful only while
   * `reasoning.status` is `thinking`, which is the only path that reads it.
   */
  reasoningStartedAt: number;
}

export function createInitialState(): StreamState {
  return {
    phase: 'idle',
    streamId: null,
    lastEventId: 0,
    text: '',
    reasoning: null,
    toolCalls: [],
    queue: null,
    usage: null,
    error: null,
    redaction: null,
    truncated: null,
    generationPhase: null,
    clientToolCalls: [],
    reconnects: 0,
    seenIds: new Set(),
    timeline: [],
    hasGap: false,
    malformedStreak: 0,
    malformedFailures: new Map(),
    reasoningStartedAt: 0,
  };
}

/**
 * One full copy. Everything mutable is duplicated so the caller's state is
 * never touched, and everything scalar rides along on the spread. The batch
 * entry points pay this cost once per call, not once per frame, which is what
 * keeps a 5000 token flush linear.
 */
function cloneState(state: StreamState): StreamState {
  return {
    ...state,
    reasoning:
      state.reasoning === null
        ? null
        : {
            ...state.reasoning,
            ...(state.reasoning.toolCalls === undefined
              ? {}
              : { toolCalls: state.reasoning.toolCalls.map((call) => ({ ...call })) }),
          },
    toolCalls: state.toolCalls.map((call) => ({
      ...call,
      ...(call.steps === undefined ? {} : { steps: [...call.steps] }),
    })),
    queue: state.queue === null ? null : { ...state.queue },
    usage: state.usage === null ? null : { ...state.usage },
    error: state.error === null ? null : { ...state.error },
    redaction: state.redaction === null ? null : { ...state.redaction },
    clientToolCalls: state.clientToolCalls.map((call) => ({ ...call })),
    seenIds: new Set(state.seenIds),
    timeline: [...state.timeline],
    malformedFailures: new Map(state.malformedFailures),
  };
}

/** Apply one frame. Pure. */
export function reduceFrame(state: StreamState, frame: DecodedFrame, at: number): StreamState {
  return reduceFrames(state, [frame], at);
}

/** Apply one chunk's worth of frames on a single copy. Pure from the outside. */
export function reduceFrames(
  state: StreamState,
  frames: readonly DecodedFrame[],
  at: number,
): StreamState {
  const draft = cloneState(state);
  for (const frame of frames) applyFrame(draft, frame, at);
  return draft;
}

function applyFrame(draft: StreamState, frame: DecodedFrame, at: number): void {
  // Rule 7. Terminal states absorb. Nothing after done, error, or cancelled
  // changes what the reader has been told, but the discard itself is recorded.
  if (isTerminalPhase(draft.phase)) {
    draft.timeline.push({ kind: 'post_terminal', at, description: describeFrame(frame) });
    return;
  }

  switch (frame.kind) {
    case 'keepalive': {
      draft.timeline.push({ kind: 'keepalive', at });
      return;
    }

    case 'sentinel': {
      // Skipped before dispatch and before cursor delivery. It advances
      // nothing, which the codec already guarantees by holding the pending id.
      draft.timeline.push({ kind: 'sentinel', at });
      return;
    }

    case 'malformed': {
      applyMalformed(draft, frame.reason, at);
      return;
    }

    case 'event': {
      applyEventFrame(draft, frame, at);
      return;
    }
  }
}

function describeFrame(frame: DecodedFrame): string {
  switch (frame.kind) {
    case 'event':
      return `event ${frame.event}${frame.id === null ? '' : ` id ${String(frame.id)}`}`;
    case 'sentinel':
      return 'sentinel';
    case 'keepalive':
      return 'keepalive';
    case 'malformed':
      return `malformed frame (${frame.reason})`;
  }
}

function applyMalformed(draft: StreamState, reason: string, at: number): void {
  // The codec dropped the pending id, so the id is inferred: the cursor holds
  // on a malformed frame, so the frame occupied the next slot, and a replay
  // fails at the same key on every attempt. That is what makes the count
  // survive reconnects.
  const inferredId = draft.lastEventId + 1;
  const failures = (draft.malformedFailures.get(inferredId) ?? 0) + 1;
  draft.malformedFailures.set(inferredId, failures);
  draft.malformedStreak += 1;
  draft.timeline.push({ kind: 'malformed', at, reason, failures });

  // Rule 4 first. Three broken frames in a row on one connection means the
  // stream itself cannot be trusted, and that is a different diagnosis from
  // one frame that never parses. The streak is reset by any well formed frame
  // and by every reconnect, so rule 9 below is what fires across replays.
  if (draft.malformedStreak >= 3) {
    draft.phase = 'error';
    draft.error = {
      code: 'unknown',
      message: 'Three malformed frames arrived in a row. The stream was stopped.',
    };
    return;
  }

  // Rule 9. Without this, a frame that is malformed on the wire is malformed
  // on every replay, and the client reconnects forever. The gap is permanent,
  // visible, and stated; the cursor moves past the poisoned id so the next
  // replay proceeds.
  if (failures >= 3) {
    draft.timeline.push({
      kind: 'gap',
      at,
      fromId: draft.lastEventId,
      toId: inferredId + 1,
      permanent: true,
    });
    draft.hasGap = true;
    draft.lastEventId = inferredId;
    draft.seenIds.add(inferredId);
    draft.malformedStreak = 0;
  }
}

function applyEventFrame(
  draft: StreamState,
  frame: Extract<DecodedFrame, { kind: 'event' }>,
  at: number,
): void {
  const { id } = frame;

  if (id !== null) {
    // Rule 1. Replays land here: everything already applied is discarded
    // whole, so text is never appended twice.
    if (draft.seenIds.has(id)) {
      draft.timeline.push({ kind: 'duplicate', at, id });
      return;
    }
    // Rule 2. An id at or below the cursor that was never applied is stale:
    // applying it now would splice old output into new. It is dropped, and
    // the drop is visible.
    if (id <= draft.lastEventId) {
      draft.timeline.push({ kind: 'stale', at, id });
      return;
    }
  }

  const event = toNormalized(frame);

  // Rule 5. Unrecognised names and recognised events with unusable payloads
  // are both dropped by the adapter, and the drop is recorded rather than
  // thrown. The cursor still advances: the reference reaches its cursor step
  // for every dispatched data line, and a resume should not replay a frame
  // the client will only drop again.
  if (event === null) {
    draft.timeline.push({ kind: 'unknown_event', at, event: frame.event, id });
    if (id !== null) advanceCursor(draft, id, at);
    return;
  }

  // Rule 3. The skip is marked before the frame is applied, so the timeline
  // reads in arrival order: the gap, then the frame that revealed it.
  if (id !== null) advanceCursor(draft, id, at);

  draft.malformedStreak = 0;
  applyEvent(draft, event, at);
  draft.timeline.push({ kind: 'frame', at, id, type: event.type, bytes: payloadBytes(frame) });
}

/** Payload size for the tape's tick height. Character count of the JSON text. */
function payloadBytes(frame: Extract<DecodedFrame, { kind: 'event' }>): number {
  return JSON.stringify(frame.data ?? null).length;
}

function advanceCursor(draft: StreamState, id: number, at: number): void {
  if (id > draft.lastEventId + 1) {
    draft.timeline.push({
      kind: 'gap',
      at,
      fromId: draft.lastEventId,
      toId: id,
      permanent: false,
    });
    draft.hasGap = true;
  }
  draft.seenIds.add(id);
  draft.lastEventId = id;
}

function applyEvent(draft: StreamState, event: WireBackedEvent, at: number): void {
  switch (event.type) {
    case 'response.created': {
      draft.streamId = event.streamId;
      return;
    }

    case 'response.output_text.delta': {
      draft.text += event.delta;
      draft.phase = 'streaming';
      draft.queue = null;
      closeReasoning(draft, at);
      return;
    }

    case 'response.output_text.replace': {
      // Rule 6, and rule 8 of the specification. The previous text is not
      // retained: a retraction that quietly keeps the retracted text around
      // has not retracted it. What the reader is told is the category list
      // and the amount removed, which is exactly what redaction records.
      const previousLength = draft.text.length;
      // The published member keeps categories optional, but the adapter
      // materialises it on both wire replaces, so the fallback is type
      // narrowing rather than a reachable branch. Its adapter tests hold
      // that guarantee at 100 percent branches.
      /* v8 ignore next */
      const categories = event.categories ?? [];
      const bare = event.content === undefined && categories.length === 0;
      draft.text = event.content ?? '';
      draft.redaction = { categories, replacedAt: at, previousLength, bare };
      draft.timeline.push({
        kind: 'replace',
        at,
        charactersRemoved: previousLength,
        categories,
        bare,
      });
      return;
    }

    case 'response.queued': {
      draft.phase = 'queued';
      draft.queue = {
        position: event.position,
        estimatedWaitSeconds: event.estimatedWaitSeconds,
      };
      return;
    }

    case 'response.status': {
      if (event.status === 'thinking') {
        draft.phase = 'thinking';
        draft.queue = null;
        openReasoning(draft, at);
      } else if (event.status === 'streaming') {
        draft.phase = 'streaming';
        draft.queue = null;
      }
      // Any other status, the open union's tail included, is recorded by the
      // frame entry and changes nothing. Phase transitions come only from
      // signals this client understands.
      return;
    }

    case 'response.generation_status': {
      draft.generationPhase = event.phase;
      const reasoning = draft.reasoning ?? {};
      reasoning.thinkingContent =
        reasoning.thinkingContent === undefined
          ? event.phase
          : `${reasoning.thinkingContent}\n${event.phase}`;
      draft.reasoning = reasoning;
      return;
    }

    case 'response.tool_call.started': {
      const call: ToolCall = { tool: event.tool, status: 'running', startedAt: at };
      // The union member carries inputOrArgs; the accumulated ToolCall keeps
      // input and args apart, which is how it is published.
      if (typeof event.inputOrArgs === 'string') {
        call.input = event.inputOrArgs;
      } else if (event.inputOrArgs !== undefined) {
        call.args = event.inputOrArgs;
      }
      if (event.output !== undefined) call.output = event.output;
      draft.toolCalls.push(call);
      return;
    }

    case 'response.tool_call.progress': {
      const call = findRunningCall(draft, event.tool);
      call.progress = event.message;
      call.steps = [...(call.steps ?? []), event.message];
      return;
    }

    case 'response.tool_call.completed': {
      const call = findRunningCall(draft, event.tool);
      call.status = event.status;
      call.endedAt = at;
      if (event.input !== undefined) call.input = event.input;
      if (event.output !== undefined) call.output = event.output;
      return;
    }

    case 'response.client_tool_call': {
      draft.clientToolCalls.push({
        name: event.name,
        args: event.args,
        ...(event.callId === undefined ? {} : { callId: event.callId }),
      });
      return;
    }

    case 'response.usage': {
      draft.usage = event.usage;
      return;
    }

    case 'response.truncated': {
      draft.truncated = event.reason;
      return;
    }

    case 'response.completed': {
      closeReasoning(draft, at);
      draft.phase = 'done';
      draft.queue = null;
      return;
    }

    case 'response.error': {
      closeReasoning(draft, at);
      draft.phase = 'error';
      draft.queue = null;
      draft.error = { code: event.code, message: event.message };
      return;
    }
  }
}

/**
 * The call a progress or result frame belongs to. The wire tool events carry
 * no call id, so the match is the most recent running call with the same tool
 * name. A progress or result with no antecedent creates one, because a frame
 * that arrived out of order is still information the reader should see.
 */
function findRunningCall(draft: StreamState, tool: string): ToolCall {
  const running = [...draft.toolCalls]
    .reverse()
    .find((call) => call.tool === tool && call.status === 'running');
  if (running !== undefined) return running;
  const created: ToolCall = { tool, status: 'running' };
  draft.toolCalls.push(created);
  return created;
}

function openReasoning(draft: StreamState, at: number): void {
  // A window that is already open stays open; a fresh one starts the clock.
  if (draft.reasoning?.status !== 'thinking') draft.reasoningStartedAt = at;
  const reasoning = draft.reasoning ?? {};
  reasoning.status = 'thinking';
  draft.reasoning = reasoning;
}

/** Thinking is over the moment visible output starts, or the stream ends. */
function closeReasoning(draft: StreamState, at: number): void {
  if (draft.reasoning === null || draft.reasoning.status !== 'thinking') return;
  draft.reasoning.status = 'done';
  draft.reasoning.totalElapsedMs = at - draft.reasoningStartedAt;
  if (draft.toolCalls.length > 0) {
    draft.reasoning.toolCalls = draft.toolCalls.map((call) => ({ ...call }));
  }
}

/*
 * Machine driven transitions. The machine owns connections, timers, and
 * retries, but it does not own state: it asks for these, each one a pure copy,
 * so that every phase change and every timeline entry still has exactly one
 * author.
 */

/** The first connection is opening. */
export function markConnecting(state: StreamState): StreamState {
  const draft = cloneState(state);
  draft.phase = 'connecting';
  return draft;
}

/** A reconnect attempt is starting. Resets the malformed streak, rule 4. */
export function markReconnecting(state: StreamState, at: number, attempt: number): StreamState {
  const draft = cloneState(state);
  draft.phase = 'reconnecting';
  draft.reconnects += 1;
  draft.malformedStreak = 0;
  draft.timeline.push({ kind: 'reconnect', at, attempt, lastEventId: draft.lastEventId });
  return draft;
}

/** A watchdog fired. The phase change, if any, is the caller's next step. */
export function markWatchdogFired(
  state: StreamState,
  at: number,
  watchdog: 'byte' | 'content',
): StreamState {
  const draft = cloneState(state);
  draft.timeline.push({ kind: 'watchdog', at, watchdog });
  if (watchdog === 'content') draft.phase = 'stalled';
  return draft;
}

/** The user cancelled. Partial text is retained. */
export function markCancelled(state: StreamState, at: number): StreamState {
  const draft = cloneState(state);
  draft.phase = 'cancelled';
  draft.queue = null;
  draft.timeline.push({ kind: 'cancelled', at });
  return draft;
}

/** Every reconnect attempt failed. Terminal, and retryable by hand. */
export function markReconnectsExhausted(state: StreamState, attempts: number): StreamState {
  const draft = cloneState(state);
  draft.phase = 'error';
  draft.error = {
    code: 'upstream_unavailable',
    message:
      `The connection was lost and ${String(attempts)} reconnect attempts failed. ` +
      `Retry to continue from event ${String(draft.lastEventId)}.`,
  };
  return draft;
}

/** A manual retry from error, stalled, or cancelled. Keeps text and cursor. */
export function markRetry(state: StreamState, at: number): StreamState {
  const draft = cloneState(state);
  draft.phase = 'reconnecting';
  draft.error = null;
  draft.malformedStreak = 0;
  draft.timeline.push({ kind: 'retry', at });
  return draft;
}

/**
 * A reconnect attempt was answered 429 with a usable Retry-After. The wait is
 * surfaced as a rate_limited error in a non terminal phase, so the UI can show
 * "Retrying in {n} seconds" while the machine sits out the window. The value
 * is client state derived from the header; the wire error payload carries no
 * retry timing. See docs/DECISIONS.md entry 16.
 */
export function markRateLimited(state: StreamState, retryAfterSeconds: number): StreamState {
  const draft = cloneState(state);
  draft.phase = 'reconnecting';
  draft.error = {
    code: 'rate_limited',
    message: `Too many requests. Retrying in ${String(retryAfterSeconds)} seconds.`,
    retryAfterSeconds,
  };
  return draft;
}

/** The rate limit window passed and the reconnect is going ahead. */
export function clearRateLimit(state: StreamState): StreamState {
  if (state.error === null || state.error.code !== 'rate_limited') return state;
  const draft = cloneState(state);
  draft.error = null;
  return draft;
}
